import {i18n} from '@yourcoach/i18n';

import type {BNCourseApiModel} from '../../../../api/types/bnrks/models';
import type {DateTimeObj} from '../../../../utils/datetime';
import type {DateTimeModelFactory} from '../../Date/DateTime.factory';
import type {DateTimeModel} from '../../Date/DateTime.model';
import type {CourseModel} from '../Course.model';
import type {CourseMapperJrpc} from './Course.mapper.jrpc';

import {CourseRestart} from '../CourseRestart';

export class CourseRestartMapperJrpc {
  private restart: CourseModel.Restart = {
    date: null,
    history: [],
  };

  constructor(private dateTimeModelFactory: DateTimeModelFactory) {}

  map(
    props: CourseMapperJrpc.ApiData,
    shouldSkip: boolean,
  ): CourseModel['restart'] {
    if (shouldSkip) {
      return null;
    }

    this.parseDate(props);
    this.parseDates(props);

    return this.restart;
  }

  private dateMapper(dateObj: DateTimeObj) {
    return this.dateTimeModelFactory.createFromDateTimeObject(dateObj);
  }

  private parseDate(props: CourseMapperJrpc.ApiData) {
    if (!props.restart_date) {
      return;
    }

    this.restart.date = this.dateTimeModelFactory.createFromDateTimeObject(
      props.restart_date,
    );
  }

  private parseDates(props: CourseMapperJrpc.ApiData) {
    if (!props.restarted_dates?.length) {
      return;
    }

    const dates = this.withFutureRestart(
      props.restarted_dates.map(d =>
        this.dateTimeModelFactory.createFromDateTimeObject(d),
      ),
    );

    const reasons =
      (props.restarted_on as BNCourseApiModel['restarted_on']) || [];

    const reasonsLookup = new Map<number, string>();

    reasons.forEach(item => {
      const [reason, dateObj] = Object.entries(item)[0]!;

      reasonsLookup.set(
        this.dateMapper(dateObj).value,
        this.reasonMapper(reason),
      );
    });

    this.restart.history = dates.map(
      date => new CourseRestart(date, reasonsLookup.get(date.value)),
    );
  }

  private reasonMapper(reason: string) {
    let label = '';

    switch (reason) {
      case 'automatically':
        label = i18n.t('shared:auto_renew');
        break;
      case 'by_client_themselves':
        label = i18n.t('shared:by_client');
        break;
      case 'by_user_request':
        label = i18n.t('shared:by_coach');
        break;
      default:
        break;
    }

    return label.toLowerCase();
  }

  private withFutureRestart(dates: DateTimeModel[]) {
    const restartDate = this.restart.date;

    if (!restartDate) {
      return dates;
    }

    const now = this.dateTimeModelFactory.now();
    const shouldAddFutureRestart = restartDate && restartDate.lt(now);

    if (shouldAddFutureRestart) {
      dates.push(restartDate);
    }

    return dates;
  }
}
